import React from 'react'
import {get, isEmpty, isEqual, isNil, isString, mapValues, sortBy, trim} from 'lodash'
import Button from 'components/button'
import iconNames from 'theme/icons'
import themeVars from 'theme/themeVars'
import {getNumberString} from 'helpers/financial'

export const getInputAppend = (input, currencySymbol) => {
  return get(input, 'options.format') === 'currency'
    ? currencySymbol || ''
    : get(input, 'options.uom', '')
}

export const getInputAccuracy = (input, pricingAccuracy) => {
  if (get(input, 'options.format') === 'currency') {
    return pricingAccuracy
  }
  return get(input, 'options.accuracy', 0)
}

export const isDropdownInput = (input) =>
  get(input, 'type') === 'string' && !isNil(get(input, 'options.choices'))

const getEmptyInputValue = (input) => {
  if (get(input, 'type') === 'number') return 0
  if (get(input, 'type') === 'boolean') return false
  return ''
}

export const getInitialResourceFormValues = (inputs = [], resource = null) =>
  inputs.reduce((values, input) => {
    const value = get(resource, input.name)
    if (!isNil(value)) {
      values[input.name] = value
    } else if (!isNil(input.default)) {
      values[input.name] = input.default
    } else {
      values[input.name] = getEmptyInputValue(input)
    }
    return values
  }, {})

export const isResourceFormValid = (inputs = [], values = {}) =>
  inputs
    .filter((input) => input.required)
    .every((input) => {
      const value = values[input.name]
      if (input.type === 'string') {
        return typeof value === 'string' && value.trim() !== ''
      }
      return !isNil(value)
    })

const DEFAULT_COLUMN_SIZE = 130
const NAME_COLUMN_SIZE = 200
const TOGGLE_COLUMN_SIZE = 40

export const formatResourceValue = (input, value, currencySymbol, pricingAccuracy) => {
  if (isNil(value) || value === '') {
    return ''
  }
  if (get(input, 'type') !== 'number') {
    return String(value)
  }

  const formatted = getNumberString(value, getInputAccuracy(input, pricingAccuracy))
  const append = getInputAppend(input, currencySymbol)

  return append ? `${formatted} ${append}` : formatted
}

export const buildResourcePayload = (resourceType, values = {}, modes = [], resource = null) => {
  const modeInputs = get(resourceType, 'modeInputs', [])

  const payload = {
    type: get(resourceType, 'name'),
    ...mapValues(values, (value) => isString(value) ? trim(value) : value),
  }
  if (!isNil(resource)) payload.id = resource.id
  if (!isEmpty(modeInputs)) {
    payload.modes = sortBy(modes, 'sequence')
      .map((mode) => getInitialResourceFormValues(modeInputs, mode))
  }
  return payload
}

export const hasResourceChanged = (resourceType, resource, payload) => {
  const saved = buildResourcePayload(
    resourceType,
    getInitialResourceFormValues(get(resourceType, 'inputs', []), resource),
    get(resource, 'modes', []),
    resource
  )

  return !isEqual(saved, payload)
}

export const sortModes = (modes = []) =>
  sortBy(
    modes.map((mode, index) => isNil(get(mode, 'sequence')) ? {...mode, sequence: index} : mode),
    [(mode) => get(mode, 'dormant') ? 1 : 0, 'sequence']
  )

export const getModeColumns = (modeInputs = [], currencySymbol, pricingAccuracy, onToggleDormant) =>
  modeInputs.reduce((columns, input, index) => {
    const name = get(input, 'name')
    const label = get(input, 'label', name)
    const isNumber = get(input, 'type') === 'number'
    const append = getInputAppend(input, currencySymbol)

    if (get(input, 'type') === 'boolean') {
      columns[name] = {
        key: name,
        label,
        alias: label,
        size: TOGGLE_COLUMN_SIZE,
        align: 'center',
        visible: true,
        editable: false,
        order: index,
        data: (mode) => mode.isNewRow ? null : (
          <Button
            flat
            className={get(mode, name) ? 'restore-mode' : 'dormant-mode'}
            title={get(mode, name) ? 'Live Mode' : 'Dormant Mode'}
            icon={get(mode, name) ? iconNames.live : iconNames.dormant}
            iconColor={get(mode, name) ? themeVars.palette.goodColor : themeVars.palette.errorColor}
            onClick={() => onToggleDormant(mode)}
          />
        ),
        export: () => null,
      }
      return columns
    }

    columns[name] = {
      key: name,
      label,
      alias: label,
      size: name === 'name' ? NAME_COLUMN_SIZE : DEFAULT_COLUMN_SIZE,
      align: isNumber ? 'right' : 'left',
      visible: true,
      editable: true,
      required: get(input, 'required', false),
      dataType: isNumber ? 'Number' : 'String',
      dataTypeForEntity: (mode) => isNumber && !get(mode, 'dormant') ? 'Number' : 'String',
      data: (mode) => get(mode, 'dormant')
        ? (
          <span style={{color: themeVars.palette.disabledTextColor}}>
            {formatResourceValue(input, get(mode, name), currencySymbol, pricingAccuracy)}
          </span>
        )
        : get(mode, name),
      setData: (mode, value) => {
        if (name === 'name') {
          mode.name = trim(value) || label
        } else {
          mode[name] = isNumber ? value : trim(value)
        }
      },
      order: index,
    }

    if (isNumber) {
      columns[name].options = {
        accuracy: getInputAccuracy(input, pricingAccuracy),
        step: get(input, 'options.step', 1),
        min: get(input, 'options.min', 0),
        append: append ? ` ${append}` : '',
      }
    }

    return columns
  }, {})

export const getResourceColumns = (resourceType, currencySymbol, pricingAccuracy) => {
  const inputs = get(resourceType, 'inputs', [])

  return inputs.reduce((columns, input, index) => {
    const name = get(input, 'name')
    const isNumber = get(input, 'type') === 'number'

    columns[name] = {
      key: name,
      label: get(input, 'label', name),
      size: name === 'name' ? NAME_COLUMN_SIZE : DEFAULT_COLUMN_SIZE,
      visible: true,
      align: isNumber ? 'right' : 'left',
      order: index,
      data: (resource) => formatResourceValue(input, get(resource, name), currencySymbol, pricingAccuracy),
    }

    return columns
  }, {})
}
